import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import useDebounce from '@/hooks/useDebounce'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import {
    ChevronLeft,
    ChevronRight,
    ChevronsLeft,
    ChevronsRight,
    Loader2,
    Search,
    User,
    UserMinus,
    Users,
    X,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { Control, Controller, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Checkbox, Radio, Select, Typogaphy } from 'vnetwork-ui'
import { getInternalSourcePreview, getInternalSources } from '../../../services'
import {
    MAILER_INTERNAL_MODES,
    MAILER_SOURCE_TYPES,
    MailerInternalMode,
    MailerReceiver,
    MailerTemplateVariableItem,
} from '../../../types'
import ReceiverImporter from '../../shared/ReceiverImporter'
import InternalFiltersSection from './InternalFiltersSection'
import { FormValues } from '../hooks/useCampaignForm'
import { Pagination } from '@/types/shared'

interface ReceiversSectionProps {
    control: Control<FormValues>
    sourceType: 'internal' | 'external'
    selectedSource: string | null
    receivers: MailerReceiver[]
    receiverVariables: MailerTemplateVariableItem[]
    isNew: boolean
    filtersPreview: {
        receivers: (MailerReceiver & { customer_id?: number })[]
        pagination?: Pagination
    } | null
    excludedCustomerIds: number[]
    filtersPage: number
    filtersPageSize: number
    filtersTotalWithoutSearch: number
    onReceiversChange: (receivers: MailerReceiver[], columns: string[]) => void
    onSourceChange: (sourceId: string | null) => void
    onInternalModeChange: (mode: MailerInternalMode) => void
    onPreviewFilters: (page?: number, search?: string) => void
    onFiltersPageChange: (page: number) => void
    onToggleExcludeCustomer: (customerId: number) => void
    isPreviewingFilters?: boolean
}

const ReceiversSection: React.FC<ReceiversSectionProps> = ({
    control,
    sourceType,
    selectedSource,
    receivers,
    receiverVariables,
    isNew,
    filtersPreview,
    excludedCustomerIds,
    filtersPage,
    filtersPageSize,
    filtersTotalWithoutSearch,
    onReceiversChange,
    onSourceChange,
    onInternalModeChange,
    onPreviewFilters,
    onFiltersPageChange,
    onToggleExcludeCustomer,
    isPreviewingFilters = false,
}) => {
    const { t } = useTranslation()
    const [searchValue, setSearchValue] = useState('')
    const [pageInput, setPageInput] = useState('')
    const debouncedSearch = useDebounce(searchValue, 500)

    // Watch internal mode from form
    const internalMode = useWatch({
        control,
        name: 'mailer_campaign_internal_mode',
    })

    // Fetch internal sources list
    const { data: internalSources = [], isLoading: isLoadingSources } =
        useQueryWithHandler({
            queryKey: [QUERY_KEYS.MAILER_INTERNAL_SOURCES],
            queryFn: getInternalSources,
            enabled: sourceType === 'internal' && internalMode === 'preset',
        })

    // Fetch preview when source is selected (preset mode)
    const { data: sourcePreview, isLoading: isLoadingPreview } =
        useQueryWithHandler({
            queryKey: [
                QUERY_KEYS.MAILER_INTERNAL_SOURCES,
                selectedSource,
                'preview',
            ],
            queryFn: () => getInternalSourcePreview(Number(selectedSource)),
            enabled:
                sourceType === 'internal' &&
                internalMode === 'preset' &&
                !!selectedSource,
        })

    const sourceOptions = internalSources.map((source) => ({
        value: String(source.mailer_internal_source_id),
        label: source.mailer_internal_source_name,
    }))

    // Get preview data based on mode
    const previewData =
        internalMode === 'preset' ? sourcePreview : filtersPreview
    const isLoading =
        internalMode === 'preset' ? isLoadingPreview : isPreviewingFilters

    // For filters mode: use filtersTotalWithoutSearch for display (not affected by search)
    // For pagination: use pagination.total (affected by search)
    const displayTotal =
        internalMode === 'filters'
            ? filtersTotalWithoutSearch
            : (sourcePreview?.total ?? 0)
    const paginationTotal =
        internalMode === 'filters'
            ? (filtersPreview?.pagination?.total ?? 0)
            : (sourcePreview?.total ?? 0)
    const effectiveTotal = displayTotal - excludedCustomerIds.length

    // Calculate total for header (excluding excluded customers)
    const getTotal = () => {
        if (sourceType === 'external' && receivers.length > 0) {
            return `(${receivers.length})`
        }
        if (sourceType === 'internal' && previewData) {
            return `(${effectiveTotal})`
        }
        return ''
    }

    // Pagination helpers - use paginationTotal (affected by search) for pagination
    const totalPages =
        paginationTotal > 0 ? Math.ceil(paginationTotal / filtersPageSize) : 0

    // Auto search when debounced value changes
    useEffect(() => {
        if (internalMode === 'filters' && filtersPreview) {
            onPreviewFilters(1, debouncedSearch)
        }
    }, [debouncedSearch])

    // Handle clear search
    const handleClearSearch = () => {
        setSearchValue('')
    }

    // Handle page input
    const handlePageInputKeyDown = (
        e: React.KeyboardEvent<HTMLInputElement>
    ) => {
        if (e.key === 'Enter') {
            const page = parseInt(pageInput, 10)
            if (page >= 1 && page <= totalPages) {
                onFiltersPageChange(page)
                setPageInput('')
            }
        }
    }

    return (
        <div className="space-y-2" data-tour="receivers-section">
            <div className="flex items-center gap-2">
                <User size={16} className="text-green-600" />
                <Typogaphy className="font-medium text-primary capitalize flex items-center">
                    {t('mailerCampaign:receivers')} {getTotal()}
                </Typogaphy>
            </div>

            {/* Source Type Selection */}
            <Controller
                name="mailer_campaign_source_type"
                control={control}
                render={({ field }) => (
                    <div className="flex items-center gap-4">
                        {MAILER_SOURCE_TYPES.map((source) => (
                            <label
                                key={source.value}
                                className={`flex items-center gap-2 ${!isNew ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
                            >
                                <Radio
                                    size="small"
                                    checked={field.value === source.value}
                                    onChange={() =>
                                        field.onChange(source.value)
                                    }
                                    disabled={!isNew}
                                />
                                <span className="text-sm">
                                    {t(source.value)}
                                </span>
                            </label>
                        ))}
                    </div>
                )}
            />

            {/* External Source - File Import */}
            {sourceType === 'external' && (
                <ReceiverImporter
                    receivers={receivers}
                    onReceiversChange={onReceiversChange}
                    customerVariables={receiverVariables}
                    hasExistingReceivers={!isNew && receivers.length > 0}
                />
            )}

            {/* Internal Source */}
            {sourceType === 'internal' && (
                <div className="space-y-3">
                    {/* Internal Mode Selection */}
                    <div className="flex items-center gap-4 p-2 bg-gray-50 rounded-lg">
                        {MAILER_INTERNAL_MODES.map((mode) => (
                            <label
                                key={mode.value}
                                className={`flex items-center gap-2 ${!isNew ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
                            >
                                <Radio
                                    size="small"
                                    checked={internalMode === mode.value}
                                    onChange={() =>
                                        onInternalModeChange(
                                            mode.value as MailerInternalMode
                                        )
                                    }
                                    disabled={!isNew}
                                />
                                <span className="text-sm">
                                    {t(
                                        mode.value === 'preset'
                                            ? 'preset_source'
                                            : 'custom_filters'
                                    )}
                                </span>
                            </label>
                        ))}
                    </div>

                    {/* Preset Mode - Select Internal Source */}
                    {internalMode === 'preset' && (
                        <div>
                            <Typogaphy className="text-xs text-gray-600 mb-1">
                                {t('select_internal_source')}
                            </Typogaphy>
                            <Select
                                size="small"
                                placeholder={t(
                                    'select_internal_source_placeholder'
                                )}
                                options={sourceOptions}
                                value={selectedSource || ''}
                                onChange={(value) =>
                                    onSourceChange((value as string) || null)
                                }
                                loading={isLoadingSources}
                                disabled={!isNew}
                            />
                        </div>
                    )}

                    {/* Filters Mode */}
                    {internalMode === 'filters' && (
                        <InternalFiltersSection
                            control={control}
                            isPreviewingFilters={isPreviewingFilters}
                            onPreviewFilters={onPreviewFilters}
                        />
                    )}

                    {/* Preview Section */}
                    {((internalMode === 'preset' && selectedSource) ||
                        (internalMode === 'filters' && filtersPreview)) && (
                        <div className="border rounded-lg overflow-hidden">
                            <div className="bg-gray-50 px-3 py-2 border-b flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <Users
                                        size={14}
                                        className="text-green-600"
                                    />
                                    <Typogaphy className="text-sm font-medium text-gray-700">
                                        {t('preview_customers')}
                                    </Typogaphy>
                                </div>
                                <div className="flex items-center gap-3">
                                    {/* Excluded count badge */}
                                    {excludedCustomerIds.length > 0 && (
                                        <span className="flex items-center gap-1 text-xs text-orange-600 bg-orange-50 px-2 py-0.5 rounded">
                                            <UserMinus size={12} />
                                            {t('excluded')}:{' '}
                                            {excludedCustomerIds.length}
                                        </span>
                                    )}
                                    {previewData && (
                                        <span className="text-xs text-gray-500">
                                            {t('total')}:{' '}
                                            <strong>{effectiveTotal}</strong>{' '}
                                            {t('customers')}
                                            {excludedCustomerIds.length > 0 && (
                                                <span className="text-gray-400 ml-1">
                                                    ({displayTotal} -{' '}
                                                    {excludedCustomerIds.length}
                                                    )
                                                </span>
                                            )}
                                        </span>
                                    )}
                                </div>
                            </div>

                            {/* Search Bar */}
                            {internalMode === 'filters' && (
                                <div className="px-3 py-2 border-b bg-white">
                                    <div className="relative max-w-xs">
                                        <input
                                            type="text"
                                            value={searchValue}
                                            onChange={(e) =>
                                                setSearchValue(e.target.value)
                                            }
                                            placeholder={t(
                                                'search_by_email_name'
                                            )}
                                            className="w-full pl-8 pr-8 py-1.5 text-sm border rounded-md focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
                                        />
                                        <Search
                                            size={14}
                                            className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400"
                                        />
                                        {searchValue && (
                                            <button
                                                type="button"
                                                onClick={handleClearSearch}
                                                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                            >
                                                <X size={14} />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Show loading only on first load, otherwise show data with overlay */}
                            {!previewData?.receivers?.length && isLoading ? (
                                <div className="flex items-center justify-center py-6">
                                    <Loader2
                                        className="animate-spin text-primary"
                                        size={20}
                                    />
                                </div>
                            ) : previewData &&
                              previewData.receivers.length > 0 ? (
                                <>
                                    <div className="max-h-[300px] overflow-auto relative">
                                        {/* Loading overlay */}
                                        {isLoading && (
                                            <div className="absolute inset-0 bg-white/60 flex items-center justify-center z-10">
                                                <Loader2
                                                    className="animate-spin text-primary"
                                                    size={20}
                                                />
                                            </div>
                                        )}
                                        <table className="min-w-full divide-y divide-gray-200 text-sm">
                                            <thead className="bg-gray-50 sticky top-0">
                                                <tr>
                                                    <th className="px-2 py-1.5 text-center text-xs font-semibold text-gray-600 w-10">
                                                        <Typogaphy className="text-[10px] text-gray-500">
                                                            {t('include')}
                                                        </Typogaphy>
                                                    </th>
                                                    <th className="px-3 py-1.5 text-left text-xs font-semibold text-gray-600 w-8">
                                                        #
                                                    </th>
                                                    <th className="px-3 py-1.5 text-left text-xs font-semibold text-gray-600">
                                                        Email
                                                    </th>
                                                    <th className="px-3 py-1.5 text-left text-xs font-semibold text-gray-600">
                                                        {t('name')}
                                                    </th>
                                                    <th className="px-3 py-1.5 text-left text-xs font-semibold text-gray-600">
                                                        {t('company')}
                                                    </th>
                                                    <th className="px-3 py-1.5 text-left text-xs font-semibold text-gray-600">
                                                        {t('phone')}
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-200">
                                                {previewData.receivers.map(
                                                    (receiver, idx) => {
                                                        const customerId =
                                                            typeof receiver.customer_id ===
                                                            'number'
                                                                ? receiver.customer_id
                                                                : undefined
                                                        const isExcluded =
                                                            customerId
                                                                ? excludedCustomerIds.includes(
                                                                      customerId
                                                                  )
                                                                : false
                                                        const rowNum =
                                                            (filtersPage - 1) *
                                                                filtersPageSize +
                                                            idx +
                                                            1

                                                        return (
                                                            <tr
                                                                key={
                                                                    customerId ||
                                                                    receiver.email ||
                                                                    idx
                                                                }
                                                                className={`${
                                                                    isExcluded
                                                                        ? 'bg-red-50 opacity-60'
                                                                        : idx %
                                                                                2 ===
                                                                            0
                                                                          ? 'bg-white'
                                                                          : 'bg-gray-50'
                                                                }`}
                                                            >
                                                                <td className="px-2 py-1.5 text-center">
                                                                    {customerId && (
                                                                        <Checkbox
                                                                            size="small"
                                                                            checked={
                                                                                !isExcluded
                                                                            }
                                                                            onChange={() =>
                                                                                onToggleExcludeCustomer(
                                                                                    customerId
                                                                                )
                                                                            }
                                                                        />
                                                                    )}
                                                                </td>
                                                                <td className="px-3 py-1.5 text-xs text-gray-500">
                                                                    {rowNum}
                                                                </td>
                                                                <td
                                                                    className={`px-3 py-1.5 ${isExcluded ? 'line-through text-gray-400' : 'text-gray-900'}`}
                                                                >
                                                                    {
                                                                        receiver.email
                                                                    }
                                                                </td>
                                                                <td
                                                                    className={`px-3 py-1.5 ${isExcluded ? 'line-through text-gray-400' : 'text-gray-600'}`}
                                                                >
                                                                    {receiver.name ||
                                                                        '-'}
                                                                </td>
                                                                <td
                                                                    className={`px-3 py-1.5 ${isExcluded ? 'line-through text-gray-400' : 'text-gray-600'}`}
                                                                >
                                                                    {receiver.company ||
                                                                        '-'}
                                                                </td>
                                                                <td
                                                                    className={`px-3 py-1.5 ${isExcluded ? 'line-through text-gray-400' : 'text-gray-600'}`}
                                                                >
                                                                    {receiver.phone ||
                                                                        '-'}
                                                                </td>
                                                            </tr>
                                                        )
                                                    }
                                                )}
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* Pagination */}
                                    {totalPages > 1 && (
                                        <div className="px-3 py-2 bg-gray-50 border-t flex items-center justify-between">
                                            <span className="text-xs text-gray-500">
                                                {t('page')} {filtersPage} /{' '}
                                                {totalPages} ({paginationTotal}{' '}
                                                {t('records')})
                                            </span>
                                            <div className="flex items-center gap-2">
                                                {/* Page input */}
                                                <div className="flex items-center gap-1">
                                                    <span className="text-xs text-gray-500">
                                                        {t('go_to')}:
                                                    </span>
                                                    <input
                                                        type="number"
                                                        min={1}
                                                        max={totalPages}
                                                        value={pageInput}
                                                        onChange={(e) =>
                                                            setPageInput(
                                                                e.target.value
                                                            )
                                                        }
                                                        onKeyDown={
                                                            handlePageInputKeyDown
                                                        }
                                                        placeholder={String(
                                                            filtersPage
                                                        )}
                                                        className="w-14 px-2 py-0.5 text-xs border rounded focus:outline-none focus:ring-1 focus:ring-primary"
                                                    />
                                                </div>

                                                {/* Navigation buttons */}
                                                <div className="flex items-center gap-0.5">
                                                    <button
                                                        type="button"
                                                        onClick={() =>
                                                            onFiltersPageChange(
                                                                1
                                                            )
                                                        }
                                                        disabled={
                                                            filtersPage <= 1 ||
                                                            isLoading
                                                        }
                                                        className="p-1 rounded hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed"
                                                        title={t('first_page')}
                                                    >
                                                        <ChevronsLeft
                                                            size={16}
                                                        />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() =>
                                                            onFiltersPageChange(
                                                                filtersPage - 1
                                                            )
                                                        }
                                                        disabled={
                                                            filtersPage <= 1 ||
                                                            isLoading
                                                        }
                                                        className="p-1 rounded hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed"
                                                        title={t(
                                                            'previous_page'
                                                        )}
                                                    >
                                                        <ChevronLeft
                                                            size={16}
                                                        />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() =>
                                                            onFiltersPageChange(
                                                                filtersPage + 1
                                                            )
                                                        }
                                                        disabled={
                                                            filtersPage >=
                                                                totalPages ||
                                                            isLoading
                                                        }
                                                        className="p-1 rounded hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed"
                                                        title={t('next_page')}
                                                    >
                                                        <ChevronRight
                                                            size={16}
                                                        />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() =>
                                                            onFiltersPageChange(
                                                                totalPages
                                                            )
                                                        }
                                                        disabled={
                                                            filtersPage >=
                                                                totalPages ||
                                                            isLoading
                                                        }
                                                        className="p-1 rounded hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed"
                                                        title={t('last_page')}
                                                    >
                                                        <ChevronsRight
                                                            size={16}
                                                        />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </>
                            ) : (
                                <div className="py-6 text-center text-gray-500 text-sm">
                                    {t('no_customers_found')}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}

export default ReceiversSection
